import React from 'react';
import { shallowEqual } from 'react-redux';
import { Box } from '@apia/theme';
import { useAppSelector } from '../../../../../hooks/hooks';
import useContext from '../../../../../hooks/useContext';
import { useRegisteredRenderDebug } from '../../../../../hooks/useRenderDebug';
import store from '../../../../../store';
import { dataGridActions } from '../../../../../store/dataGridSlice/dataGridSlice';
import propsStore from '../../../../../store/propsStore/propsStore';
import { arrayOrArray, noNaN } from '../../../../../utils/util';
import { SimpleButton } from '../../../../common';
import { notify } from '../../../../common/notifications/util';
import Pagination from '../../../../common/Pagination';
import fieldsStore from '../../fieldsStore/fieldsStore';
import { DataGridIdentifier } from './context';

const DataGridFooter = ({ maximizationId }: { maximizationId: string }) => {
  const { gridId } = useContext(DataGridIdentifier);

  const {
    currentPage,
    hasRaisedTotalRegisters,
    isLoading,
    isPaginated,
    pages,
    rowsCount,
    shouldAlterOnlyLastPage,
    shouldHideAddButton,
    shouldHideButtons,
    shouldHideDeleteButton,
    shouldHideEditionButtons,
    shouldHideOrderButton: shouldHideOrderButtons,
    hideMaximizeButton,
  } = useAppSelector((global) => {
    const state = global.dataGridSlice[gridId];
    const grid = state.context.grid.field;
    const firstField = arrayOrArray(grid.field)[0];
    const props = propsStore.getFieldProps(gridId);
    const pagesInner = noNaN(grid.pages);
    const currentPageInner = noNaN(grid.curPage);
    const isLastPage = currentPageInner < pagesInner;
    const shouldAlterOnlyLastPageInner =
      state?.context.grid.field.properties.alterOnlyLastPage;
    return {
      currentPage: currentPageInner,
      hasRaisedTotalRegisters:
        noNaN(grid.properties.maxRecords ?? Number.POSITIVE_INFINITY) <=
        noNaN(grid.rowCount ?? state.rows.length),
      isLoading: state.loading > 0 || state.updating,
      isPaginated: grid.properties.paged,
      pages: pagesInner,
      rowsCount:
        arrayOrArray(firstField.fieldValue ?? firstField.row).length - 1,
      shouldHideButtons:
        String(
          grid.properties.hideGridButtons ||
            state.context.form.isFormReadOnly ||
            props?.readonly,
        ) === 'true' || grid.properties.gridQuery,
      shouldHideEditionButtons: grid.properties.hideIncludeButton,
      shouldHideOrderButton:
        grid.properties.hideOrderButton ||
        (shouldAlterOnlyLastPageInner && isLastPage),
      shouldHideAddButton: grid.properties.hideAddButton,
      shouldHideDeleteButton:
        grid.properties.hideDelButton ||
        (shouldAlterOnlyLastPageInner && isLastPage),
      shouldAlterOnlyLastPage: shouldAlterOnlyLastPageInner,
      hideMaximizeButton:
        grid.properties?.hideMaximizeButton === 'T' ||
        grid.properties?.hideMaximizeButton === 'true',
    };
  }, shallowEqual);
  const sortedSelectedRows = useAppSelector((global) => {
    const state = global.dataGridSlice[gridId];
    return [...state.selectedRows].sort();
  }, shallowEqual);

  useRegisteredRenderDebug('dataGridFooter', {
    currentPage,
    hasRaisedTotalRegisters,
    isLoading,
    isPaginated,
    pages,
    rowsCount,
    shouldAlterOnlyLastPage,
    shouldHideAddButton,
    shouldHideButtons,
    shouldHideDeleteButton,
    shouldHideEditionButtons,
    shouldHideOrderButtons,
    sortedSelectedRows,
    gridId,
  });

  if (!isPaginated && shouldHideButtons) return null;

  return (
    <Box className="grid__footer">
      <Box className="grid__pagination__wrapper">
        <Pagination
          className="grid__pagination"
          currentPage={currentPage}
          disableReduced={isPaginated}
          disabled={isLoading}
          hideRefreshButton
          listId={maximizationId}
          hideMaximizeButton={hideMaximizeButton}
          onPageChange={(page) => {
            void store.dispatch(dataGridActions.gotoPage({ gridId, page }));
          }}
          onRefresh={() => {}}
          pageCount={pages}
          showMaximizeOnSmallBreakpoints
          variant="datagrid"
        />
      </Box>
      {!shouldHideButtons && (
        <Box className="dataGrid__footer__buttons">
          {!shouldHideOrderButtons && (
            <SimpleButton
              onClick={() =>
                fieldsStore.getById(gridId).fireEvent('gridSortUp')
              }
              {...window.getVariant('outline')}
              size="sm"
              className={`dataGrid__sortUpButton grid_${gridId}`}
              disabled={
                isLoading ||
                (sortedSelectedRows[0] <= 0 &&
                  ((!isPaginated && currentPage === 0) ||
                    (isPaginated && currentPage === 1))) ||
                sortedSelectedRows.length === 0 ||
                (sortedSelectedRows[0] <= 0 && shouldAlterOnlyLastPage)
              }
            >
              {window.GRID_BTN_UP}
            </SimpleButton>
          )}
          {!shouldHideOrderButtons && (
            <SimpleButton
              onClick={() =>
                fieldsStore.getById(gridId).fireEvent('gridSortDown')
              }
              {...window.getVariant('outline')}
              className={`dataGrid__sortDownButton grid_${gridId}`}
              size="sm"
              disabled={
                isLoading ||
                (sortedSelectedRows[sortedSelectedRows.length - 1] ===
                  rowsCount &&
                  noNaN(currentPage) === noNaN(pages)) ||
                sortedSelectedRows.length === 0
              }
            >
              {window.GRID_BTN_DOWN}
            </SimpleButton>
          )}
          {!shouldHideAddButton && !shouldHideEditionButtons && (
            <SimpleButton
              disabled={isLoading}
              {...window.getVariant('outline')}
              size="sm"
              className={`dataGrid__addButton grid_${gridId}`}
              onClick={() => {
                if (hasRaisedTotalRegisters)
                  notify({
                    type: 'warning',
                    message: window.GRID_MAX_ROW_REACHED,
                  });
                else {
                  fieldsStore.getById(gridId).fireEvent('gridAdd');
                }
              }}
            >
              {window.GRID_BTN_ADD}
            </SimpleButton>
          )}
          {!shouldHideDeleteButton && !shouldHideEditionButtons && (
            <SimpleButton
              {...window.getVariant('outline-danger-sm')}
              className={`dataGrid__deleteButton grid_${gridId}`}
              onClick={() => {
                fieldsStore.getById(gridId).fireEvent('gridDelete');
              }}
              disabled={
                isLoading ||
                sortedSelectedRows.length === 0 ||
                (sortedSelectedRows.length === 1 && sortedSelectedRows[0] < 0)
              }
            >
              {window.GRID_BTN_DELETE}
            </SimpleButton>
          )}
        </Box>
      )}
    </Box>
  );
};

export default React.memo(DataGridFooter);
